import { Injectable, inject } from '@angular/core';
import { EMPTY, Observable, from, of } from 'rxjs';
import { switchMap, share, filter, finalize } from 'rxjs/operators';
import { ComponentStore } from '@ngrx/component-store';
import { PCP_LOGGER } from '@pcp/logging';
import { TokensAndDecodedToken } from '../interfaces/token-error';
import { AuthConfigService } from './auth-config.service';
import { AuthRetryStrategyService } from './auth-retry-strategy.service';

/**
 * Internal state for the token refresh orchestrator
 */
interface TokenRefreshState {
    isRefreshing: boolean;
}

/**
 * Interface for token refresh operations that the orchestrator delegates to
 */
export interface TokenRefreshDelegate {
    performTokenRefresh(token: string, refreshToken: string): Observable<TokensAndDecodedToken>;
}

/**
 * Interface for auth service operations that the orchestrator needs
 */
export interface AuthServiceOperations {
    isTokenRefreshRequired(): boolean;
    refreshTokenIfRequired(): Observable<TokensAndDecodedToken>;
}

/**
 * Service responsible for orchestrating the complex token refresh flow.
 * Coordinates network monitoring, wake detection, offline handling, and retry strategies.
 */
@Injectable({
    providedIn: 'root'
})
export class TokenRefreshOrchestratorService extends ComponentStore<TokenRefreshState> {

    private readonly authConfig = inject(AuthConfigService);
    private readonly retryStrategy = inject(AuthRetryStrategyService);
    private readonly log = inject(PCP_LOGGER).createScoped('auth');

    private pendingTokenRefreshRequest: Observable<TokensAndDecodedToken> | undefined;
    private authService: AuthServiceOperations | undefined;

    constructor() {
        super({
            isRefreshing: false,
        });

        this.setupNetworkEventHandlers();
    }

    /**
     * Orchestrate token refresh with network checking, wake detection, and retry logic.
     * Returns existing pending request if one is already in progress.
     */
    orchestrateTokenRefresh(
        token: string,
        refreshToken: string,
        delegate: TokenRefreshDelegate
    ): Observable<TokensAndDecodedToken> {
        // Return existing pending request if one exists
        if (this.pendingTokenRefreshRequest) {
            return this.pendingTokenRefreshRequest;
        }

        this.patchState({ isRefreshing: true });

        // Create the orchestrated refresh observable
        this.pendingTokenRefreshRequest = from(
            this.performNetworkChecksAndRefresh(token, refreshToken, delegate)
        ).pipe(
            switchMap(result => result),
            finalize(() => {
                // Clear pending request when Observable completes or errors
                this.pendingTokenRefreshRequest = undefined;
                this.patchState({ isRefreshing: false });
            }),
            share()
        );

        return this.pendingTokenRefreshRequest;
    }

    /**
     * Check if a token refresh is currently in progress
     */
    isRefreshing(): boolean {
        return this.get().isRefreshing;
    }

    /**
     * Clear any pending refresh request (used for cleanup)
     */
    clearPendingRequest(): void {
        this.pendingTokenRefreshRequest = undefined;
        this.patchState({
            isRefreshing: false
        });
    }

    /**
     * Set the auth service reference for triggering refresh checks when coming online
     */
    setAuthService(authService: AuthServiceOperations): void {
        this.authService = authService;
    }

    /**
     * Get observable stream for refreshing state
     */
    readonly isRefreshing$ = this.select(state => state.isRefreshing);


    /**
     * Perform network checks before delegating to actual token refresh
     */
    private async performNetworkChecksAndRefresh(
        token: string,
        refreshToken: string,
        delegate: TokenRefreshDelegate
    ): Promise<Observable<TokensAndDecodedToken>> {

        // Simple offline check - return non-erroring observable if offline
        if (!navigator.onLine) {
            this.log.info('Device offline - skipping token refresh (will retry automatically via interval)');
            return EMPTY;
        }

        // Network is ready, proceed with actual refresh with retry logic
        const retryConfig = this.authConfig.getRetryConfig();

        return delegate.performTokenRefresh(token, refreshToken).pipe(
            this.retryStrategy.createTokenRefreshRetryStrategy({
                maxAttempts: retryConfig.maxAttempts,
                baseDelayMs: retryConfig.baseDelayMs,
                networkErrorDelayMs: retryConfig.networkErrorDelayMs
            })
        );
    }


    /**
     * Setup network event handlers for cleanup and online refresh checks
     */
    private setupNetworkEventHandlers(): void {
        // Handle offline events
        window.addEventListener('offline', () => {
            this.log.info('Network went offline - clearing pending requests');
            this.clearPendingRequest();
        });

        // Handle online events
        window.addEventListener('online', () => {
            this.log.info('Network came online - checking if token refresh is needed');
            if (this.authService?.isTokenRefreshRequired()) {
                this.log.info('Token refresh required after coming online - triggering refresh');
                this.authService.refreshTokenIfRequired().subscribe({
                    next: () => this.log.info('Token refresh successful after coming online'),
                    error: (error) => this.log.warn('Token refresh failed after coming online', { error })
                });
            }
        });
    }
}
